import { computePutsRr1 } from "./targetsV2.js";
import {
  upDownVolumeAvgs,
  blendedRsVsSpy,
  hasActiveModule3Bottom,
  gradeFromScoreAndGate,
  formatNeedsReason,
} from "./checklistCommon.js";

function fail(id, shortLabel, needLabel) {
  return { id, shortLabel, needLabel: needLabel || shortLabel };
}

export function evaluatePutsChecklist(result) {
  const passed = [];
  const failed = [];
  const price = result?.price ?? result?.closes?.[0];
  const ema50 = result?.sm50 ?? result?.ema50;
  const ema200 = result?.sm200 ?? result?.ema200;
  const buyScore = result?.buyScore ?? 0;
  const gate = result?.gate?.label ?? "WAIT";
  const gateBlocked = result?.gate?.blocked || gate === "BLOCKED";

  const empty = {
    status: "NONE",
    grade: null,
    passed,
    failed,
    reason: "",
    checklist_failed: [],
    putsRr1: null,
    allPass: false,
  };

  if (price == null) return empty;

  const trendOk =
    (ema50 != null && price < ema50) &&
    ((ema50 != null && ema200 != null && ema50 < ema200) ||
      (ema200 != null && price < ema200));
  if (trendOk) passed.push("Downtrend structure");
  else failed.push(fail("trend", "No downtrend structure", "price below EMA50 & bearish stack"));

  const cmf = result?.cmf;
  const flowOk = cmf != null && cmf <= -0.05 && !result?.cmfDiv?.bullishDiv;
  if (flowOk) passed.push("Money leaving");
  else failed.push(fail("flow", "Money not leaving", "CMF ≤ −0.05 without bullish CMF divergence"));

  const { upAvg, downAvg } = upDownVolumeAvgs(result?.closes, result?.volumes, 10);
  const volOk = upAvg != null && downAvg != null && downAvg >= upAvg;
  if (volOk) passed.push("Down-day volume dominant");
  else failed.push(fail("volume", "Volume not confirming shorts", "down-day volume ≥ up-day volume"));

  const rsi = result?.rsi;
  const rsiOk = rsi != null && rsi >= 25 && rsi <= 55;
  if (rsiOk) passed.push("RSI in puts zone");
  else failed.push(fail("rsi", "RSI outside 25–55", "RSI 25–55"));

  const rsSpread = blendedRsVsSpy(result);
  const rsOk = rsSpread != null && rsSpread < 0;
  if (rsOk) passed.push("Weak vs SPY");
  else failed.push(fail("rs", "Not weak vs SPY", "RS vs SPY negative"));

  const bottomOk = !hasActiveModule3Bottom(result);
  if (bottomOk) passed.push("No bottom reversal pattern");
  else failed.push(fail("bottom_pattern", "Bottom pattern active", "no active bottom pattern"));

  const scoreOk = buyScore <= -3;
  if (scoreOk) passed.push("Score floor");
  else failed.push(fail("score_floor", "Score above −3 floor", "score ≤ −3 sanity floor"));

  const putsRr1 =
    result.putsRr1 ??
    computePutsRr1({
      price,
      highs: result.highs,
      lows: result.lows,
      closes: result.closes,
      sm50: ema50,
      atr: result.atr,
      range52w: result.range52w,
    });

  const flag = result.options_flag ?? result.optionsFlag ?? "";
  if (result.optionsBlock && !/^earnings/i.test(String(flag))) {
    failed.push(fail("options", result.optionsAdvisory || "Options block", "options clear"));
  }

  const nonTrendFails = failed.filter((f) => f.id !== "trend");
  let status = "NONE";
  let reason = "";

  if (!trendOk) {
    status = "NONE";
  } else if (nonTrendFails.length === 0) {
    status = "SETUP";
  } else if (nonTrendFails.length <= 2) {
    status = "FORMING";
    reason = formatNeedsReason(nonTrendFails);
  } else {
    status = "NONE";
  }

  let grade = null;
  if (status === "SETUP" || status === "ENTRY_OK") {
    grade = gradeFromScoreAndGate(result, "short");
  }

  if (status === "SETUP") {
    const rrOk = putsRr1 != null && putsRr1 >= 1.5;
    const gateOk = !gateBlocked;
    if (rrOk && gateOk) {
      status = "ENTRY_OK";
    } else if (gateBlocked) {
      reason = result.gate?.failedCritical?.[0]?.shortLabel ?? "Gate blocked";
    } else if (!rrOk) {
      reason = putsRr1 != null ? `Reward:risk ${putsRr1.toFixed(1)}:1 — below 1.5:1` : "Reward:risk too low";
    }
    grade = gradeFromScoreAndGate(result, "short");
  }

  return {
    status,
    grade,
    passed,
    failed,
    reason,
    checklist_failed: failed.map((f) => f.id),
    putsRr1,
    allPass: status === "ENTRY_OK",
    rsSpread,
    trendOk,
  };
}
